import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Card, CardBody, Button, Alert } from 'reactstrap';

const ApplicationStatus = ({ status }) => {
	const navigate = useNavigate();

	const getStatusConfig = () => {
		switch (status) {
			case 'Submitted':
				return {
					title: 'Application Submitted Successfully',
					icon: '✅',
					color: 'success',
					message: 'Your application has been successfully submitted and is currently under review.',
					details: [
						'Your application is being reviewed by our team',
						'You will be notified via email about the next steps',
						'Please check your email regularly for updates'
					]
				};

			case 'Review 1 - In Progress':
			case 'Review 1 - Completed':
			case 'Review 2 - In Progress':
			case 'Review 2 - Completed':
				return {
					title: 'Application Under Review',
					icon: '🔍',
					color: 'info',
					message: 'Your application is currently being reviewed by our evaluation committee.',
					details: [
						`Current Status: ${status}`,
						'Our team is carefully evaluating your application',
						'You will be notified via email once the review is complete',
						'Please be patient as this process may take some time'
					]
				};

			case 'Shortlisted':
				return {
					title: 'Congratulations! You Have Been Shortlisted',
					icon: '🎉',
					color: 'success',
					message: 'Great news! Your application has been shortlisted for the fellowship program.',
					details: [
						'Your application has been reviewed and shortlisted',
						'You will receive further instructions via email soon',
						'Please keep checking your email for next steps'
					]
				};

			case 'Sent for Annexure':
				return {
					title: 'Additional Documents Required',
					icon: '📄',
					color: 'warning',
					message: 'Please submit the required annexure documents to proceed with your application.',
					details: [
						'Your application requires additional documentation',
						'Click the button below to upload the required documents',
						'Please submit the documents at your earliest convenience'
					],
					showButton: true,
					buttonText: 'Submit Documents',
					buttonAction: () => navigate('/submitdocuments')
				};

			case 'Annexure Received':
				return {
					title: 'Documents Received',
					icon: '✅',
					color: 'success',
					message: 'Thank you for submitting the required documents.',
					details: [
						'Your additional documents have been received',
						'Your application is now being reviewed for final decision',
						'You will be notified via email about the final outcome'
					]
				};

			case 'Accepted':
				return {
					title: 'Congratulations! You Have Been Accepted',
					icon: '🎊',
					color: 'success',
					message: 'We are delighted to inform you that you have been accepted into our fellowship program!',
					details: [
						'Your application has been approved',
						'Further instructions will be sent to your email',
						'Welcome to the fellowship program!'
					]
				};

			case 'Rejected':
				return {
					title: 'Application Status Update',
					icon: '📋',
					color: 'danger',
					message: 'We regret to inform you that your application was not selected for this fellowship program.',
					details: [
						'After careful consideration, we are unable to offer you a position at this time',
						'We encourage you to apply again in future cycles',
						'Thank you for your interest in our program'
					]
				};

			default:
				return {
					title: 'Application Status',
					icon: '📝',
					color: 'secondary',
					message: 'Your application status is being processed.',
					details: [
						'Please check back later for updates',
						'You will be notified via email for any changes'
					]
				};
		}
	};

	const config = getStatusConfig();

	return (
		<div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', paddingTop: '2rem', paddingBottom: '2rem' }}>
			<Container className="my-5">
				<Card className="shadow-lg border-0">
					<CardBody className="p-5">
						<div className="text-center mb-4">
							<div style={{ fontSize: '4rem' }} className="mb-3">
								{config.icon}
							</div>
							<h2 className="mb-3">{config.title}</h2>
							<Alert color={config.color} className="mb-4">
								<strong>{config.message}</strong>
							</Alert>
						</div>

						<div className="mb-4">
							<h5 className="mb-3">Important Information:</h5>
							<ul className="list-unstyled">
								{config.details.map((detail, index) => (
									<li key={index} className="mb-2">
										<i className="bi bi-check-circle-fill text-primary me-2"></i>
										{detail}
									</li>
								))}
							</ul>
						</div>

						<div className="text-center mt-4">
							{config.showButton && config.buttonAction && (
								<Button
									color="primary"
									size="lg"
									onClick={config.buttonAction}
									className="me-3"
								>
									<i className="bi bi-upload me-2"></i>
									{config.buttonText}
								</Button>
							)}
							<Button
								color="secondary"
								size="lg"
								onClick={() => navigate('/')}
								outline
							>
								<i className="bi bi-house me-2"></i>
								Go to Home
							</Button>
						</div>
					</CardBody>
				</Card>
			</Container>
		</div>
	);
};

export default ApplicationStatus;
